export const apiPaths = {
  auth: {
    register: () => "/auth/register",
    login: () => "/auth/login",
    logout: () => "/auth/logout",
    refresh: () => "/auth/refresh",
    validate: () => "/auth/validate",
    google: () => "/auth/providers/google",
    googleMobile: () => "/auth/providers/google/mobile",
    authorizeInitialize: () => "/auth/authorize/initialize",
    authorizeStatus: (id: string) => `/auth/authorize/status/${encodeURIComponent(id)}`,
    authorizeConfirm: () => "/auth/authorize/confirm",
    authorizeDeny: () => "/auth/authorize/deny",
    authorizeGoogle: () => "/auth/authorize/google",
    token: () => "/auth/token",
    tokenRefresh: () => "/auth/token/refresh",
    tokenRevoke: () => "/auth/token/revoke",
    tokenIntrospect: () => "/auth/token/introspect",
    passwordReset: () => "/auth/password/reset",
    passwordResetConfirm: () => "/auth/password/reset/confirm",
    passwordResetVerify: (token: string) =>
      `/auth/password/reset/verify/${encodeURIComponent(token)}`,
    phoneSend: () => "/auth/phone/send",
    phoneVerify: () => "/auth/phone/verify",
    phoneCheck: () => "/auth/phone/check",
    emailCheck: () => "/auth/email/check",
    emailSend: () => "/auth/email/send",
    emailVerify: () => "/auth/email/verify",
    emailVerifyChange: () => "/auth/email/verify-change",
  },
  accounts: {
    me: () => "/accounts/me",
    name: () => "/accounts/me/name",
    email: () => "/accounts/me/email",
    username: () => "/accounts/me/username",
    phoneVerify: () => "/accounts/me/phone/verify",
    emailSendVerification: () => "/accounts/me/email/send-verification-code",
    emailVerifyCode: () => "/accounts/me/email/verify-code",
    avatar: () => "/accounts/me/avatar",
    onboardingComplete: () => "/accounts/onboarding/complete",
  },
  groups: {
    root: () => "/groups",
    byId: (id: string) => `/groups/${encodeURIComponent(id)}`,
    license: (id: string) => `/groups/${encodeURIComponent(id)}/license`,
    members: (id: string) => `/groups/${encodeURIComponent(id)}/members`,
    member: (id: string, accountId: string) =>
      `/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(accountId)}`,
  },
  billings: {
    plans: () => "/billings/plans",
    plan: (lookupKey: string) => `/billings/plans/${encodeURIComponent(lookupKey)}`,
    methods: () => "/billings/methods",
    discountValidate: () => "/billings/discount/validate",
    subscription: () => "/billings/subscription",
    subscriptionPreview: () => "/billings/subscription/preview",
    subscriptionCancel: () => "/billings/subscription/cancel",
    subscriptionRenew: () => "/billings/subscription/renew",
    invoices: () => "/billings/invoices",
    invoiceDownload: (invoiceId: string) => `/billings/invoices/${invoiceId}/download`,
    invoiceRetry: (invoiceId: string) => `/billings/invoices/${invoiceId}/retry`,
    refunds: () => "/billings/refunds",
    refund: (refundId: string) => `/billings/refunds/${refundId}`,
  },
  issues: {
    root: () => "/issues",
  },
  models: {
    details: () => "/models",
    chunk: () => "/models/chunk",
    subsceneChunk: () => "/models/subscene-chunk",
    catalog: () => "/models/catalog",
  },
  devices: {
    activate: () => "/devices/activate",
    heartbeat: (deviceId: string) => `/devices/${deviceId}/heartbeat`,
    models: {
      details: (deviceId: string) => `/devices/${deviceId}/models`,
      chunk: (deviceId: string) => `/devices/${deviceId}/models/chunk`,
      subsceneChunk: (deviceId: string) =>
        `/devices/${deviceId}/models/subscene-chunk`,
      catalog: (deviceId: string) => `/devices/${deviceId}/models/catalog`,
    },
  },
  /**
   * Studio depot API paths are relative to the Studio Worker base (`/v1`).
   * Desktop main process calls Studio API with OIDC Bearer; never from renderer.
   */
  studio: {
    me: () => "/me",
    accounts: () => "/accounts",
    workspaces: () => "/workspaces",
    workspace: (id: string) => `/workspaces/${encodeURIComponent(id)}`,
    files: () => "/files",
    fileByPath: () => "/files/by-path",
    blob: (sha256: string) => `/blobs/${encodeURIComponent(sha256)}`,
    uploads: () => "/uploads",
    uploadPart: (id: string, n: number) =>
      `/uploads/${encodeURIComponent(id)}/parts/${n}`,
    uploadComplete: (id: string) =>
      `/uploads/${encodeURIComponent(id)}/complete`,
    locks: () => "/locks",
    locksHeartbeat: () => "/locks/heartbeat",
    locksSteal: () => "/locks/steal",
    changelists: () => "/changelists",
    changelist: (id: string) => `/changelists/${encodeURIComponent(id)}`,
    changelistSubmit: (id: string) =>
      `/changelists/${encodeURIComponent(id)}/submit`,
    shares: () => "/shares",
    share: (id: string) => `/shares/${encodeURIComponent(id)}`,
    promote: () => "/promote",
    events: () => "/events",
  },
} as const;
